/**
 * Extract a small palette of dominant colors from an image.
 * Uses lightweight histogram binning (4-bit-per-channel quantisation)
 * on a downscaled copy of the image, then filters near-duplicates.
 * No third-party dependency required.
 */
export function extractPalette(imgSrc, count = 5) {
    return new Promise((resolve) => {
        const img = new Image();
        img.crossOrigin = "anonymous";
        img.onload = () => {
            const size = 120;
            const canvas = document.createElement("canvas");
            canvas.width = size;
            canvas.height = size;
            const ctx = canvas.getContext("2d");
            if (!ctx) return resolve([]);
            ctx.drawImage(img, 0, 0, size, size);
            let data;
            try {
                data = ctx.getImageData(0, 0, size, size).data;
            } catch (e) {
                // Cross-origin issue — bail silently.
                return resolve([]);
            }

            const bucket = new Map();
            for (let i = 0; i < data.length; i += 4) {
                const a = data[i + 3];
                if (a < 128) continue;
                // Quantise to 4 bits per channel (16 levels each)
                const r = data[i] & 0xf0;
                const g = data[i + 1] & 0xf0;
                const b = data[i + 2] & 0xf0;
                const key = (r << 16) | (g << 8) | b;
                bucket.set(key, (bucket.get(key) || 0) + 1);
            }

            const sorted = [...bucket.entries()]
                .sort((a, b) => b[1] - a[1])
                .slice(0, count * 4);

            const palette = [];
            const distance = (a, b) => {
                const ar = (a >> 16) & 0xff,
                    ag = (a >> 8) & 0xff,
                    ab = a & 0xff;
                const br = (b >> 16) & 0xff,
                    bg = (b >> 8) & 0xff,
                    bb = b & 0xff;
                return Math.abs(ar - br) + Math.abs(ag - bg) + Math.abs(ab - bb);
            };
            for (const [color] of sorted) {
                if (palette.every((p) => distance(p, color) > 55)) {
                    palette.push(color);
                    if (palette.length >= count) break;
                }
            }

            const hex = palette.map((c) => {
                const r = ((c >> 16) & 0xff).toString(16).padStart(2, "0");
                const g = ((c >> 8) & 0xff).toString(16).padStart(2, "0");
                const b = (c & 0xff).toString(16).padStart(2, "0");
                return `#${r}${g}${b}`;
            });
            resolve(hex);
        };
        img.onerror = () => resolve([]);
        img.src = imgSrc;
    });
}
